import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import { getTaskApplications, reviewApplication } from '../../features/taskApplications/taskApplicationSlice';
import api from '../../utils/axiosConfig';
import {
    UserGroupIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    ArrowLeftIcon,
    CurrencyDollarIcon,
    CalendarIcon,
    MapPinIcon
} from '@heroicons/react/24/outline';

const CompanyTaskDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { taskApplications, loading } = useAppSelector((state) => state.taskApplications);
    const [task, setTask] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('all');
    const [currentPage] = useState(1);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState<any>(null);
    const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
    const [selectedDesignation, setSelectedDesignation] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');

    const fetchTask = useCallback(async () => {
        if (!id) return;

        try {
            const response = await api.get(`/tasks/${id}`);
            if (response.data.success) {
                setTask(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching task:', error);
            dispatch(showToast({
                message: 'Failed to fetch task details',
                type: 'error'
            }));
        }
    }, [id, dispatch]);

    const fetchApplications = useCallback(async () => {
        if (!id) return;

        try {
            await dispatch(getTaskApplications({
                taskId: id,
                status: activeTab === 'all' ? undefined : activeTab,
                page: currentPage,
                limit: 10
            }));
        } catch (error) {
            console.error('Error fetching applications:', error);
        }
    }, [id, activeTab, currentPage, dispatch]);

    useEffect(() => {
        if (id) {
            fetchTask();
            fetchApplications();
        }
    }, [id, fetchTask, fetchApplications]);

    const handleReviewApplication = async () => {
        if (!selectedApplication) return;

        try {
            await dispatch(reviewApplication({
                applicationId: selectedApplication._id,
                action: reviewAction,
                designation: selectedDesignation,
                rejectionReason: rejectionReason
            }));

            dispatch(showToast({
                message: `Application ${reviewAction}d successfully`,
                type: 'success'
            }));

            setShowReviewModal(false);
            setSelectedApplication(null);
            setSelectedDesignation('');
            setRejectionReason('');
            fetchApplications();
        } catch (error) {
            console.error('Error reviewing application:', error);
            dispatch(showToast({
                message: 'Failed to review application',
                type: 'error'
            }));
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'approved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'approved': return <CheckCircleIcon className="h-5 w-5" />;
            case 'rejected': return <XCircleIcon className="h-5 w-5" />;
            case 'pending': return <ClockIcon className="h-5 w-5" />;
            default: return <ExclamationTriangleIcon className="h-5 w-5" />;
        }
    };

    const tabs = [
        { id: 'all', label: 'All Applications', count: taskApplications?.length || 0 },
        { id: 'pending', label: 'Pending', count: taskApplications?.filter((app: any) => app.status === 'pending').length || 0 },
        { id: 'approved', label: 'Approved', count: taskApplications?.filter((app: any) => app.status === 'approved').length || 0 },
        { id: 'rejected', label: 'Rejected', count: taskApplications?.filter((app: any) => app.status === 'rejected').length || 0 }
    ];

    if (loading && !task) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading task details...</p>
                </div>
            </div>
        );
    }

    if (!task) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Task Not Found</h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">The task you're looking for doesn't exist or you don't have permission to view it.</p>
                    <button
                        onClick={() => navigate('/company/tasks')}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Back to Tasks
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/company/tasks')}
                        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
                    >
                        <ArrowLeftIcon className="h-5 w-5 mr-2" />
                        Back to Tasks
                    </button>

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                    {task.title}
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400 mb-4">
                                    {task.description}
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                                        <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                                        <span className="font-medium">₹{(task.totalBudget || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                                        <CalendarIcon className="h-5 w-5 mr-2" />
                                        <span>{new Date(task.startDate).toLocaleDateString()} - {new Date(task.endDate).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                                        <MapPinIcon className="h-5 w-5 mr-2" />
                                        <span>{task.regions?.length || 0} regions</span>
                                    </div>
                                </div>
                            </div>

                            <div className="ml-6">
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${task.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                    task.status === 'paused' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                        'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                    }`}>
                                    {task.status?.charAt(0).toUpperCase() + task.status?.slice(1)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Applications Section */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                                <UserGroupIcon className="h-6 w-6 mr-2 text-blue-600" />
                                Task Applications
                            </h2>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                {taskApplications?.length || 0} total applications
                            </span>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav className="flex space-x-8 px-6">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    {tab.label}
                                    {tab.count > 0 && (
                                        <span className="ml-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 py-0.5 px-2 rounded-full text-xs">
                                            {tab.count}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Applications List */}
                    <div className="p-6">
                        {loading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p className="text-gray-600 dark:text-gray-400">Loading applications...</p>
                            </div>
                        ) : taskApplications && taskApplications.length > 0 ? (
                            <div className="space-y-4">
                                {taskApplications
                                    .filter((app: any) => activeTab === 'all' || app.status === activeTab)
                                    .map((application: any) => (
                                        <div key={application._id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center mb-3">
                                                        <div className="flex-shrink-0">
                                                            <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                                                <span className="text-blue-600 dark:text-blue-400 font-medium text-sm">
                                                                    {application.user?.firstName?.charAt(0)}{application.user?.lastName?.charAt(0)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="ml-4">
                                                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                                                {application.user?.firstName} {application.user?.lastName}
                                                            </h3>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                {application.user?.email}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                        <div>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                <strong>Applied:</strong> {new Date(application.appliedAt).toLocaleDateString()}
                                                            </p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                <strong>Designation:</strong> {application.assignedDesignation ? application.assignedDesignation.replace('_', ' ') : 'Not assigned'}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                <strong>Region:</strong> {application.user?.region || 'Not specified'}
                                                            </p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                <strong>Phone:</strong> {application.user?.phone || 'Not provided'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {application.message && (
                                                        <div className="mb-4">
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                <strong>Message:</strong>
                                                            </p>
                                                            <p className="text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                                                                {application.message}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="ml-6 flex flex-col items-end space-y-3">
                                                    <div className="flex items-center">
                                                        {getStatusIcon(application.status)}
                                                        <span className={`ml-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                                                            {application.status?.charAt(0).toUpperCase() + application.status?.slice(1)}
                                                        </span>
                                                    </div>

                                                    {application.status === 'pending' && (
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedApplication(application);
                                                                    setReviewAction('approve');
                                                                    setSelectedDesignation(application.user.designation || '');
                                                                    setShowReviewModal(true);
                                                                }}
                                                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center"
                                                            >
                                                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                                Approve
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedApplication(application);
                                                                    setReviewAction('reject');
                                                                    setSelectedDesignation('');
                                                                    setShowReviewModal(true);
                                                                }}
                                                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center"
                                                            >
                                                                <XCircleIcon className="h-4 w-4 mr-1" />
                                                                Reject
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                    No applications found
                                </h3>
                                <p className="text-gray-500 dark:text-gray-400">
                                    {activeTab === 'all'
                                        ? "No applications have been submitted for this task yet."
                                        : `No ${activeTab} applications found.`
                                    }
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Review Modal */}
            {showReviewModal && selectedApplication && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                {reviewAction === 'approve' ? 'Approve Application' : 'Reject Application'}
                            </h3>

                            <div className="mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    <strong>Applicant:</strong> {selectedApplication.user?.firstName} {selectedApplication.user?.lastName}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    <strong>Email:</strong> {selectedApplication.user?.email}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    <strong>Profile Designation:</strong> {selectedApplication.user?.designation ? selectedApplication.user.designation.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : 'Not specified'}
                                </p>
                            </div>

                            {reviewAction === 'approve' && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Task Designation
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                        Designation automatically set from user profile. This ensures consistency across all tasks.
                                    </p>
                                    <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white">
                                        {selectedDesignation ? selectedDesignation.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'No designation'}
                                    </div>
                                </div>
                            )}

                            {reviewAction === 'reject' && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Rejection Reason (Optional)
                                    </label>
                                    <textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter reason for rejection..."
                                    />
                                </div>
                            )}

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setShowReviewModal(false);
                                        setSelectedApplication(null);
                                        setSelectedDesignation('');
                                        setRejectionReason('');
                                    }}
                                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReviewApplication}
                                    disabled={reviewAction === 'approve' && !selectedDesignation}
                                    className={`px-4 py-2 text-white rounded-lg transition-colors ${reviewAction === 'approve'
                                        ? 'bg-green-600 hover:bg-green-700 disabled:bg-gray-400'
                                        : 'bg-red-600 hover:bg-red-700'
                                        }`}
                                >
                                    {reviewAction === 'approve' ? 'Approve' : 'Reject'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyTaskDetails;
